/**
 * Theme resolution shared by every place that needs to know "dark or light"
 * (openspec/changes/unify-user-preferences, task 1.6).
 *
 * The one source of truth is the `isDarkMode` editor preference, which follows
 * the user across devices. While it has never been set (undefined) the theme
 * follows the operating system.
 */

export const PREFERENCES_CACHE_KEY = 'screenwriter_preferences';

export const systemPrefersDark = () => {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  } catch {
    return false;
  }
};

/** The effective theme for a stored `isDarkMode` value (boolean, or undefined = follow the OS). */
export const resolveDarkMode = (isDarkModePreference) => (
  typeof isDarkModePreference === 'boolean' ? isDarkModePreference : systemPrefersDark()
);

/**
 * Synchronous read for code that renders before / outside the preferences
 * provider (loading fallback, share-link and OAuth pages). Reads the provider's
 * own localStorage cache; falls back to the legacy `darkMode` key for a user who
 * hasn't loaded the app since the migration, then to the OS setting.
 */
export const getCachedDarkMode = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(PREFERENCES_CACHE_KEY) || 'null');
    if (typeof cached?.isDarkMode === 'boolean') return cached.isDarkMode;
    const legacy = localStorage.getItem('darkMode');
    if (legacy !== null) return !!JSON.parse(legacy);
  } catch {
    // fall through to the OS setting
  }
  return systemPrefersDark();
};
